import {
  OutlinedButton,
  PrimaryButton,
  TextInput,
} from "@normasteaching/norma-ui";
import { ErrorPopup } from "../../UI/Error/ErrorPopup.tsx";
import { usePopupController } from "../../HeyLight/usePopupController.ts";
import { useEffect, useRef, useState } from "react";
import {
  StripeOrderDTO,
  StripeOrderStatus,
} from "../../Stripe/StripeOrderDTO.ts";
import { useRefundStripeOrder } from "./useRefundStripeOrder.ts";
import { Modal } from "../../UI/Modal/Modal.tsx";
import { useWithdrawalRequestDate } from "../../SpeakingClassService/Withdrawal/useWithdrawalRequestDate.ts";
import { WithdrawalRequestDateField } from "../../SpeakingClassService/Withdrawal/WithdrawalRequestDateField.tsx";
import { useGetRefundInformation } from "./useGetRefundInformation.ts";
import { OrderDetailsModal } from "./StripeOrderDetailsModal.tsx";
import { OrderStatusRenderer } from "./StripeOrderStatusRenderer.tsx";
import { SplitActionButton } from "../../UI/SplitActionButton.tsx";
import { TableColumnHeader } from "../../UI/Table/TableColumnHeader.tsx";
import { SortDirection } from "../../UI/Table/listUrlParams.ts";
import {
  describeStripePaymentMethod,
  describeStripePaymentType,
} from "../../Stripe/stripePaymentLabels.ts";
import {
  OrderSortField,
  OrdersFilters,
  OrdersListQuery,
} from "../OrdersList/OrdersListQuery.ts";
import {
  OrdersTable,
  OrdersTableExtraColumn,
  OrdersTableRow,
} from "../OrdersList/OrdersTable.tsx";
import { StripePaymentFilter } from "./StripePaymentFilter.tsx";
import { describeFailingInstallments } from "../../Stripe/describeFailingInstallments.ts";
import { InstallmentsSettlementModal } from "./InstallmentsSettlement/InstallmentsSettlementModal.tsx";
import {
  canSettleInstallments,
  NOT_SETTLEABLE_REASON,
} from "./InstallmentsSettlement/canSettleInstallments.ts";

const STATUS_FILTER_OPTIONS = Object.values(StripeOrderStatus).map(
  (status) => ({
    value: status,
    label: <OrderStatusRenderer status={status} />,
  }),
);

const PAYMENT_COLUMN: OrdersTableExtraColumn<
  StripeOrderDTO,
  StripeOrderStatus
> = {
  key: "payment",
  renderHeader: (filters, updateFilters) => (
    <TableColumnHeader
      label="Pagamento"
      isFiltered={
        !!(
          filters.paymentMethods?.length ||
          filters.paymentType ||
          filters.failingInstallments
        )
      }
      renderFilter={(close) => (
        <StripePaymentFilter
          value={{
            paymentMethods: filters.paymentMethods,
            paymentType: filters.paymentType,
            failingInstallments: filters.failingInstallments,
          }}
          onApply={updateFilters}
          close={close}
        />
      )}
    />
  ),
  renderCell: (order) => {
    const failingInstallments = describeFailingInstallments(order);
    return (
      <div className="flex flex-col">
        <span className="text-gray-900 dark:text-white">
          {describeStripePaymentMethod(order.paymentMethod)}
        </span>
        <span className="text-gray-500 dark:text-gray-400 text-xs">
          {describeStripePaymentType(order)}
        </span>
        {failingInstallments && (
          <span className="text-xs font-medium text-red-600 dark:text-red-400">
            {failingInstallments}
          </span>
        )}
      </div>
    );
  },
};

function toStripeOrdersTableRow(
  order: StripeOrderDTO,
): OrdersTableRow<StripeOrderStatus> {
  return {
    id: order._id,
    createdAt: order.createdAt,
    customer: order.customerInformation,
    purchasedItem: {
      id: order.normasTeachingProductId,
      type: order.productType,
      snapshot: order.productSnapshot,
    },
    chargedAmountInCents:
      order.amountInCent.amount - (order.coupon?.value ?? 0),
    couponCode: order.coupon?.couponCode,
    status: order.status ?? StripeOrderStatus.Pending,
  };
}

const REFUNDABLE_STATUSES = [
  StripeOrderStatus.Succeeded,
  StripeOrderStatus.InstallmentPaid,
  StripeOrderStatus.InstallmentsSettledByBankTransfer,
];

function isRefundable(order: StripeOrderDTO): boolean {
  return REFUNDABLE_STATUSES.includes(order.status);
}

type RefundModalData = {
  order: StripeOrderDTO;
  refundableAmount: number;
};

export function AdminStripeOrderTable({
  orders,
  readOnly,
  query,
  onSort,
  onFiltersChange,
  onOrdersChanged,
}: {
  orders: StripeOrderDTO[];
  readOnly?: boolean;
  query: OrdersListQuery<StripeOrderStatus>;
  onSort: (field: OrderSortField, direction: SortDirection) => void;
  onFiltersChange: (filters: OrdersFilters<StripeOrderStatus>) => void;
  onOrdersChanged?: () => void;
}) {
  const [settlingOrder, setSettlingOrder] = useState<StripeOrderDTO | null>(
    null,
  );
  const [refundModalData, setRefundModalData] =
    useState<RefundModalData | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<StripeOrderDTO | null>(
    null,
  );
  const [refundType, setRefundType] = useState<"full" | "partial">("full");
  const [partialAmount, setPartialAmount] = useState("");
  const { refundStripeOrder, loading: refundLoading } = useRefundStripeOrder();
  const { getRefundInformation, loading: loadingRefundInfo } =
    useGetRefundInformation();
  const { open, title, description, openPopup, closePopup } =
    usePopupController();
  const withdrawalDate = useWithdrawalRequestDate(
    refundModalData?.order.customerInformation.email,
  );

  const isLoadingRef = useRef(false);

  useEffect(() => {
    if (!refundModalData || isLoadingRef.current) {
      return;
    }

    if (refundModalData.refundableAmount !== 0) {
      return;
    }

    const loadRefundInfo = async () => {
      isLoadingRef.current = true;
      try {
        const refundInfo = await getRefundInformation(
          refundModalData.order._id,
        );

        setRefundModalData((current) => {
          if (current?.order._id === refundModalData.order._id) {
            return {
              order: current.order,
              refundableAmount: refundInfo.refundableAmount,
            };
          }
          return current;
        });
      } catch (e) {
        const err = e as Error;
        openPopup({
          title: "Errore",
          description: `Impossibile recuperare le informazioni sul rimborso: ${err?.message ?? "Errore sconosciuto"}`,
          timeoutMs: 5000,
        });
        setRefundModalData(null);
      } finally {
        isLoadingRef.current = false;
      }
    };

    loadRefundInfo();
  }, [refundModalData, getRefundInformation, openPopup]);

  const handleRefund = async () => {
    if (!refundModalData) return;

    try {
      if (refundType === "partial") {
        const amount = parseFloat(partialAmount);
        if (isNaN(amount) || amount <= 0) {
          openPopup({
            title: "Errore",
            description: "Inserisci un importo valido per il rimborso parziale",
            timeoutMs: 5000,
          });
          return;
        }
        if (amount > refundModalData.refundableAmount / 100) {
          openPopup({
            title: "Errore",
            description:
              "L'importo del rimborso non può superare l'importo rimborsabile",
            timeoutMs: 5000,
          });
          return;
        }
        await withdrawalDate.persist();
        await refundStripeOrder(
          refundModalData.order._id,
          Math.round(amount * 100),
        );
      } else {
        await withdrawalDate.persist();
        await refundStripeOrder(refundModalData.order._id);
      }

      setRefundModalData(null);
      setPartialAmount("");
      setRefundType("full");
    } catch (e) {
      const err = e as Error;
      openPopup({
        title: "Errore",
        description: `Si è verificato un errore durante il rimborso dell'ordine: ${err?.message ?? "Errore sconosciuto"}`,
        timeoutMs: 5000,
      });
    }
  };

  const openRefundModal = (order: StripeOrderDTO) => {
    setRefundModalData({ order, refundableAmount: 0 });
    setRefundType("full");
    setPartialAmount("");
  };

  const isLoadingModal =
    loadingRefundInfo || refundModalData?.refundableAmount === 0;

  return (
    <>
      <ErrorPopup
        description={description}
        title={title}
        onClose={closePopup}
        open={open}
      />

      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          readOnly={readOnly}
          onClose={() => setSelectedOrder(null)}
          onOrderUpdated={(updatedOrder) => setSelectedOrder(updatedOrder)}
          onSettleInstallments={(order) => {
            setSelectedOrder(null);
            setSettlingOrder(order);
          }}
        />
      )}

      {settlingOrder && (
        <InstallmentsSettlementModal
          order={settlingOrder}
          onClose={() => setSettlingOrder(null)}
          onSettled={() => {
            setSettlingOrder(null);
            onOrdersChanged?.();
          }}
        />
      )}

      <Modal
        isOpen={!!refundModalData}
        onClose={() => {
          if (!refundLoading && !loadingRefundInfo) {
            setRefundModalData(null);
            setPartialAmount("");
            setRefundType("full");
          }
        }}
        title="Seleziona il tipo di rimborso"
      >
        {isLoadingModal ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">
              Caricamento informazioni rimborso...
            </span>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Scegli se effettuare un rimborso totale o parziale dell'ordine.
            </p>

            <label
              className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                refundType === "full"
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200"
              }`}
            >
              <input
                type="radio"
                name="refundType"
                value="full"
                checked={refundType === "full"}
                onChange={() => setRefundType("full")}
                className="mt-1 w-4 h-4"
                disabled={refundLoading}
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">Rimborso Totale</div>
                <div className="text-sm text-gray-600">
                  Rimborsa l'intero importo rimborsabile
                  {refundModalData && (
                    <span className="font-medium">
                      {" "}
                      (€{(refundModalData.refundableAmount / 100).toFixed(2)})
                    </span>
                  )}
                </div>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                refundType === "partial"
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200"
              }`}
            >
              <input
                type="radio"
                name="refundType"
                value="partial"
                checked={refundType === "partial"}
                onChange={() => setRefundType("partial")}
                className="mt-1 w-4 h-4"
                disabled={refundLoading}
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">
                  Rimborso Parziale
                </div>
                <div className="text-sm text-gray-600">
                  Specifica l'importo da rimborsare
                </div>
              </div>
            </label>

            {refundType === "partial" && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Importo da rimborsare (€)
                </label>
                <TextInput
                  type="number"
                  placeholder="0.00"
                  value={partialAmount}
                  onChange={(e) => setPartialAmount(e.target.value)}
                  disabled={refundLoading}
                  min={0}
                />
                {refundModalData && (
                  <p className="text-xs text-gray-500 mt-1">
                    Massimo: €
                    {(refundModalData.refundableAmount / 100).toFixed(2)}
                  </p>
                )}
              </div>
            )}

            <div className="mt-4">
              <WithdrawalRequestDateField
                date={withdrawalDate.date}
                onChange={withdrawalDate.setDate}
                existingRequest={withdrawalDate.existingRequest}
                hasWithdrawablePurchase={withdrawalDate.hasWithdrawablePurchase}
                loading={withdrawalDate.loading}
                disabled={refundLoading}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <PrimaryButton
                onClick={handleRefund}
                label="Conferma rimborso"
                loading={refundLoading}
                disabled={refundLoading}
              />
              <OutlinedButton
                onClick={() => {
                  setRefundModalData(null);
                  setPartialAmount("");
                  setRefundType("full");
                }}
                label="Annulla"
                disabled={refundLoading}
              />
            </div>
          </div>
        )}
      </Modal>

      <OrdersTable
        orders={orders}
        toRow={toStripeOrdersTableRow}
        query={query}
        onSort={onSort}
        onFiltersChange={onFiltersChange}
        statusOptions={STATUS_FILTER_OPTIONS}
        renderStatus={(status) => <OrderStatusRenderer status={status} />}
        extraColumns={[PAYMENT_COLUMN]}
        renderActions={
          readOnly
            ? undefined
            : (order) => (
                <SplitActionButton
                  mainAction={{
                    label: "Rimborsa",
                    tone: "neutral",
                    onClick: () => openRefundModal(order),
                    disabledReason: isRefundable(order)
                      ? undefined
                      : "Il rimborso è possibile solo per gli ordini completati o con rata pagata",
                  }}
                  otherActions={[
                    {
                      label: "Visualizza ordine",
                      onClick: () => setSelectedOrder(order),
                    },
                    {
                      label: "Saldo rate con bonifico",
                      onClick: () => setSettlingOrder(order),
                      disabledReason: canSettleInstallments(order)
                        ? undefined
                        : NOT_SETTLEABLE_REASON,
                    },
                  ]}
                />
              )
        }
        onSelectOrder={setSelectedOrder}
      />
    </>
  );
}
